/* ==========================================================================
   SARAURA — NAVIGATION
   assets/sr-nav.js   ·   pairs with assets/sr-nav.css. The markup it drives is
   inlined in index / about / product / collection (header + #srn-menu).

     <script src="assets/sr-nav.js" defer></script>

   Vanilla, no dependencies, safe to load with or without assets/sr.js.

   What it owns
     1. the menu takeover: open, close, focus trap, scroll lock, focus return
     2. the category preview cross-fade
     3. the header's hide-on-down / reveal-on-up — but ONLY if sr.js is not
        on the page, because sr.js already does exactly that and two drivers
        toggling the same class from the same scroll data would fight at the
        jitter threshold

   What it does not own
     the cart. That button carries sr.js's own data-sr-drawer-open attribute
     and is left entirely alone.

   Nothing here is required for the navigation to be usable. With this file
   missing or broken, the <noscript> block in each page's header is not
   rendered (scripting is on) — which is why the trigger is also a real
   <button> that fails closed rather than a link that goes nowhere. See the
   integration note about that trade-off.
   ========================================================================== */

(function () {
  'use strict';

  var doc  = document;
  var root = doc.documentElement;

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled])' +
                  ':not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),' +
                  '[tabindex]:not([tabindex="-1"])';

  function qs(sel, ctx)  { return (ctx || doc).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || doc).querySelectorAll(sel)); }

  /* Read live rather than cached: a visitor can flip the OS setting with the
     page already open, and the menu should respect that on the next open. */
  function reducedMotion() {
    return window.matchMedia &&
           window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }


  /* ======================================================================
     1  TAKEOVER
     ====================================================================== */

  function initTakeover() {
    var panel = qs('#srn-menu');
    if (!panel) return;

    var triggers = qsa('[data-srn-open]');
    var closers  = qsa('[data-srn-close]', panel);
    var isOpen   = false;
    var restore  = null;

    /* Built before anything is wired up: the trigger's warm-on-intent
       listener holds a direct reference to preview.warm. */
    var preview = createPreview(panel);

    var header = qs('.srn-header');

    /* Longest stagger (6 x 52ms) plus one transition (520ms), plus room for
       a slow frame. Kept next to the CSS values it mirrors. */
    var SETTLE_MS = 1150;
    var settleTimer = 0;

    /* The header hides itself on the way down the page. If it happens to be
       hidden at the moment the menu opens, it is still hidden when the menu
       closes — and close() puts focus back on a trigger that has been
       translated off the top of the screen. So the header is pinned back
       open at both ends. Whichever driver owns .is-unpinned, sr.js or the
       one below, both leave it alone while sr-lock is set, so this holds
       for as long as the menu is up. */
    function pinHeader() {
      if (header) header.classList.remove('is-unpinned');
    }

    /* -- inert / hidden ------------------------------------------------
       Three independent locks, so one failing (inert is unsupported on
       older Safari) still leaves the closed menu untabbable. */
    function setInert(on) {
      if (on) {
        panel.setAttribute('aria-hidden', 'true');
      } else {
        panel.removeAttribute('aria-hidden');
      }
      if ('inert' in panel) panel.inert = on;
    }

    /* -- scroll lock ---------------------------------------------------
       The scrollbar is measured into --sr-sbw and paid back as padding, so
       the page does not jump sideways as it disappears. Same class and
       same variable sr.js uses, so the two can never disagree about who
       owns overflow. */
    function lock(on) {
      var sbw = window.innerWidth - root.clientWidth;
      root.style.setProperty('--sr-sbw', (on && sbw > 0) ? sbw + 'px' : '0px');
      root.classList.toggle('sr-lock', on);
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      restore = doc.activeElement;

      setInert(false);
      lock(true);
      pinHeader();

      preview.warm();          // make sure the tiles are decoded before the fade
      preview.reset();         // first category active by default, incl. on touch

      /* No requestAnimationFrame dance here, and that is deliberate. The
         closed panel is visibility:hidden, not display:none — it is laid
         out and it has real computed styles — so opacity and transform
         already have start values and transition straight from a single
         class change. Deferring by a frame would only mean focusing a
         panel that is still hidden, which silently does nothing. */
      panel.classList.add('is-open');

      /* Backstop. The staggered entrance is decoration, and decoration is
         never allowed to be the reason a link cannot be read: if anything
         delays the frame the transitions start on — a slow device, a heavy
         decode, a backgrounded tab — this forces the finished state after
         the sequence should have completed. When the entrance ran normally
         it has already finished by now and this changes nothing. */
      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        panel.classList.add('is-settled');
      }, SETTLE_MS);

      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });

      /* Focus the dialog itself rather than a control inside it: the panel
         carries the accessible name, so a screen reader announces "Site
         menu, dialog" instead of dropping the visitor onto "Close" — which
         is at the far end of the screen from where they just tapped.
         This has to come after is-open lands, or the panel is still
         visibility:hidden and therefore not focusable. */
      panel.focus({ preventScroll: true });
    }

    function close(opts) {
      if (!isOpen) return;
      isOpen = false;

      clearTimeout(settleTimer);
      panel.classList.remove('is-open', 'is-settled');
      lock(false);
      pinHeader();     // focus is about to go back to the trigger — show it
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });

      /* Focus has to leave before the panel goes inert, or it falls to
         <body> and the next Tab restarts from the top of the document.

         <body> and <html> are not restore targets, they are the absence of
         one: that is what document.activeElement reads as when the menu was
         opened from SRNav.open() rather than from a click. Falling back to
         the trigger keeps the tab ring where the visitor left it either
         way. */
      var back = restore;
      if (!back || !back.isConnected || back === doc.body || back === root) {
        back = triggers[0];
      }
      restore = null;
      if (!(opts && opts.navigating) && back && back.focus) {
        back.focus({ preventScroll: true });
      }

      setInert(true);
    }

    function toggle() { isOpen ? close() : open(); }

    /* -- focus trap ----------------------------------------------------- */
    function trapTab(e) {
      var items = qsa(FOCUSABLE, panel).filter(function (el) {
        return el.offsetParent !== null || el === doc.activeElement;
      });
      if (!items.length) { e.preventDefault(); panel.focus(); return; }

      var first = items[0];
      var last  = items[items.length - 1];
      var here  = doc.activeElement;

      if (e.shiftKey) {
        if (here === first || here === panel || !panel.contains(here)) {
          e.preventDefault(); last.focus();
        }
      } else if (here === last) {
        e.preventDefault(); first.focus();
      }
    }

    doc.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') trapTab(e);
    });

    /* Belt and braces: anything that manages to move focus out of an open
       menu — a browser find bar, an extension — gets pulled back. */
    doc.addEventListener('focusin', function (e) {
      if (isOpen && !panel.contains(e.target)) {
        panel.focus({ preventScroll: true });
      }
    });

    /* -- wiring --------------------------------------------------------- */
    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) { e.preventDefault(); toggle(); });
      /* Start fetching the tiles on intent, not on page load: the menu is
         very often never opened at all. */
      ['pointerenter', 'focus', 'touchstart'].forEach(function (ev) {
        t.addEventListener(ev, preview.warm, { passive: true, once: true });
      });
    });

    closers.forEach(function (c) {
      c.addEventListener('click', function (e) { e.preventDefault(); close(); });
    });

    /* A link inside the menu closes it before it navigates, so returning
       with the back button never lands on a menu the visitor did not open.
       No focus restore — the browser is about to move the document. */
    qsa('a[href]', panel).forEach(function (a) {
      a.addEventListener('click', function () { close({ navigating: true }); });
    });

    /* In-page jumps and history moves both count as leaving. */
    window.addEventListener('hashchange', function () { close({ navigating: true }); });
    window.addEventListener('popstate',   function () { close({ navigating: true }); });

    /* Start closed and inert regardless of what the markup shipped with. */
    panel.classList.remove('is-open');
    setInert(true);

    window.SRNav = { open: open, close: close, toggle: toggle,
                     isOpen: function () { return isOpen; } };
  }


  /* ======================================================================
     2  CATEGORY PREVIEW

     Pointer: the panel follows hover and keyboard focus.
     Touch: there is no hover, so the first category is showing before a
     finger lands, and the panel swaps on pointerdown — the moment of
     contact, not the moment of release. The link's own click is never
     intercepted, so a tap still navigates on the first tap. Nobody is
     asked to press a category twice to go there.
     ====================================================================== */

  function createPreview(panel) {
    var frame   = qs('.srn-prev__frame', panel);
    var caption = qs('.srn-prev__caption', panel);
    var imgs    = frame ? qsa('.srn-prev__img', frame) : [];
    var cats    = qsa('.srn-cat', panel);
    var warmed  = false;
    var current = -1;

    function show(i) {
      if (i === current || i < 0 || i >= imgs.length) return;
      current = i;

      imgs.forEach(function (img, n) { img.classList.toggle('is-active', n === i); });
      cats.forEach(function (c, n) { c.classList.toggle('is-active', n === i); });

      if (caption && cats[i]) {
        var name = qs('.srn-cat__name', cats[i]);
        caption.textContent = name ? name.textContent.trim() : '';
      }
    }

    /* The tiles ship as loading="lazy" inside a visibility:hidden panel,
       which means the browser will not have fetched them. Flipping to
       eager on first intent starts the fetch a beat before the menu is
       actually on screen.

       Only the tile that is about to be visible is decode()d up front.
       These are two-megabyte photographs, and decoding all five at once
       occupied the main thread long enough to push out the frame the
       entrance transition starts on — which left the category names sitting
       at opacity 0 for most of a second. The other four are decoded when
       the browser is next idle, by which time the menu has finished
       opening and a cross-fade to any of them is instant. */
    function warm() {
      if (warmed) return;
      warmed = true;

      imgs.forEach(function (img) { img.loading = 'eager'; });

      var first = imgs[current > -1 ? current : 0];
      if (first && first.decode) first.decode().catch(function () {});

      var rest = function () {
        imgs.forEach(function (img) {
          if (img !== first && img.decode) img.decode().catch(function () {});
        });
      };
      if (window.requestIdleCallback) requestIdleCallback(rest, { timeout: 2000 });
      else setTimeout(rest, 1200);
    }

    /* Default: whichever category is the current page, else the first. */
    function reset() {
      var start = 0;
      for (var i = 0; i < cats.length; i++) {
        if (cats[i].hasAttribute('aria-current')) { start = i; break; }
      }
      show(start);
    }

    cats.forEach(function (cat, i) {
      cat.addEventListener('pointerenter', function () { show(i); });
      cat.addEventListener('pointerdown',  function () { show(i); });
      cat.addEventListener('focus',        function () { show(i); });
    });

    if (imgs.length) show(0);

    return { show: show, warm: warm, reset: reset };
  }


  /* ======================================================================
     3  HEADER SCROLL STATE

     Only installed when assets/sr.js is absent. sr.js drives .is-scrolled
     and .is-unpinned on any element matching .sr-header, and the header in
     The header markup deliberately carries that class so it is driven for
     free on pages that already load sr.js. Running both would mean two
     independent lastY trackers toggling one class.

     The selector is anchored so assets/sr-nav.js cannot match itself.
     ====================================================================== */

  function srjsPresent() {
    return !!qs('script[src$="sr.js"], script[src*="/sr.js?"], script[src*="/sr.js#"]');
  }

  function initHeaderScroll() {
    var header = qs('.srn-header');
    if (!header || srjsPresent()) return;

    var lastY   = window.scrollY;
    var ticking = false;
    var held    = 0;

    function update() {
      ticking = false;
      var y = window.scrollY;
      var floor = header.offsetHeight + 40;

      header.classList.toggle('is-scrolled', y > 8);

      /* Never hide the header while the menu is open, while focus is
         inside it, near the top of the page, or in the middle of a jump to
         an in-page anchor — that scroll is not intent to dismiss. */
      if (root.classList.contains('sr-lock') || Date.now() < held ||
          header.contains(doc.activeElement) || y <= floor) {
        header.classList.remove('is-unpinned');
        lastY = y;
        return;
      }
      if (Math.abs(y - lastY) > 6) {              // ignore trackpad jitter
        header.classList.toggle('is-unpinned', y > lastY);
        lastY = y;
      }
    }

    function onScroll() {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }
    function hold() { held = Date.now() + 900; header.classList.remove('is-unpinned'); }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('hashchange', hold);
    doc.addEventListener('click', function (e) {
      var a = e.target instanceof Element && e.target.closest('a[href*="#"]');
      if (a && a.hash && a.pathname === location.pathname) hold();
    }, true);

    update();
  }


  /* ====================================================================== */

  function boot() {
    initTakeover();
    initHeaderScroll();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}());
